/**
 * etha-filter-sheet.js — phone/tablet behaviour for the horizontal filter bar.
 *
 * Desktop taps a pill and gets an absolute dropdown (Glozin's own behaviour,
 * gated on innerWidth > 1024 inside CollapsibleBlock). Below that width Glozin
 * force-opens every group (actionInMobile) because the markup is meant to live
 * in a side drawer. We keep the pill row instead and open one group at a time
 * as a bottom sheet, so this script has three jobs:
 *
 *   1. undo the force-open, on load, on resize, and after every facet re-render
 *   2. own the pill click, because the theme's own listener sits on the heading
 *      and would slideToggle the panel we are about to turn into a sheet
 *   3. close the sheet once a value is picked — the facet applies over ajax and
 *      the scrim would otherwise sit over the page
 *
 * Job 2 uses a capture-phase listener on the document plus
 * stopImmediatePropagation: capture runs before the target's own handlers, so
 * the theme's never fires. Everything is delegated from the document and
 * re-normalised by a MutationObserver, because a facet click replaces the bar's
 * markup — listeners bound to the old nodes would die with it.
 *
 * Nothing here touches the desktop path.
 */
(() => {
  const BAR = '.collection-filter.horizontal';
  const OPEN = 'etha-sheet-open';
  const isMobile = () => window.innerWidth <= 1024;

  if (!document.querySelector(BAR)) return;

  let scrim = null;

  function getScrim() {
    if (scrim && scrim.isConnected) return scrim;
    scrim = document.createElement('div');
    scrim.className = 'etha-sheet-scrim';
    scrim.addEventListener('click', () => closeAll());
    document.body.appendChild(scrim);
    return scrim;
  }

  function closeAll() {
    document.querySelectorAll('.' + OPEN).forEach((block) => {
      block.classList.remove(OPEN);
      const content = block.querySelector('.collapsible-content');
      if (!content) return;
      // Let the slide-down play out before hiding, or the sheet vanishes
      // instead of leaving.
      setTimeout(() => {
        if (!block.classList.contains(OPEN)) content.style.display = 'none';
      }, 280);
    });
    if (scrim) scrim.classList.remove('is-visible');
    document.documentElement.classList.remove('etha-sheet-lock');
  }

  function open(block) {
    closeAll();
    const content = block.querySelector('.collapsible-content');
    if (!content) return;
    ensureChrome(block, content);
    content.style.display = 'block';
    content.style.height = '';
    getScrim().classList.add('is-visible');
    document.documentElement.classList.add('etha-sheet-lock');
    // One frame with display set but the class still off, so the transform
    // animates in from off-screen instead of snapping.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => block.classList.add(OPEN));
    });
  }

  // The sheet needs a title and a way out; the rail markup has neither.
  function ensureChrome(block, content) {
    if (content.querySelector('.etha-sheet-head')) return;
    const label = block.querySelector('.collapsible-heading span');
    const head = document.createElement('div');
    head.className = 'etha-sheet-head';
    const title = document.createElement('span');
    title.className = 'etha-sheet-title';
    // The pill label carries Glozin's active-count badge (.total_filter) when
    // the group has selections — "Shop by need 2". The sheet already says
    // "2 Selected" one row down, so take the name only.
    let text = '';
    if (label) {
      const copy = label.cloneNode(true);
      copy.querySelectorAll('.total_filter').forEach((n) => n.remove());
      text = copy.textContent.trim();
    }
    title.textContent = text;
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'etha-sheet-close';
    close.setAttribute('aria-label', 'Close');
    close.textContent = '×';
    close.addEventListener('click', () => closeAll());
    head.append(title, close);
    content.prepend(head);
  }

  // Glozin's actionInMobile() sets display:block + .active on every group under
  // 1025px, on construction and on resize. Normalise after it.
  function normalise() {
    if (!isMobile()) {
      closeAll();
      return;
    }
    document.querySelectorAll(BAR + ' collapsible-block').forEach((block) => {
      if (block.classList.contains(OPEN)) return;
      block.classList.remove('active');
      const content = block.querySelector('.collapsible-content');
      if (!content) return;
      content.style.display = 'none';
      content.style.height = '';
    });
  }

  document.addEventListener(
    'click',
    (event) => {
      if (!isMobile()) return;
      const heading = event.target.closest('.collapsible-heading');
      if (!heading || !heading.closest(BAR)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const block = heading.closest('collapsible-block');
      if (!block) return;
      if (block.classList.contains(OPEN)) closeAll();
      else open(block);
    },
    true,
  );

  // A picked value applies the facet over ajax; get out of the way so the
  // reloaded grid is visible.
  document.addEventListener('click', (event) => {
    if (!isMobile()) return;
    const row = event.target.closest('.' + OPEN + ' .filter-content .item');
    if (row) setTimeout(closeAll, 0);
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeAll();
  });

  let pending = null;
  const observer = new MutationObserver((records) => {
    // A facet render swaps the bar's markup, which brings back the
    // every-group-open state and drops the sheet chrome.
    const touched = records.some((r) => {
      const node = r.target.nodeType === 1 ? r.target : r.target.parentElement;
      return node && (node.closest(BAR) || node.querySelector?.(BAR));
    });
    if (!touched) return;
    clearTimeout(pending);
    pending = setTimeout(normalise, 60);
  });
  observer.observe(document.body, { childList: true, subtree: true });

  normalise();
  // Glozin's own resize handler also runs; ours has to land after it.
  window.addEventListener('resize', () => setTimeout(normalise, 0));
  window.addEventListener('load', () => setTimeout(normalise, 0));
})();
